//Built-in libraries
import { ChangeEvent, useEffect, useState } from 'react';

//Third party libraries
import { PlusIcon } from '@heroicons/react/outline';
import { v4 as uuidv4 } from 'uuid';

//Custom components
import Button from 'components/UI/Button';
import Confirm from 'components/UI/Modal/Confirm';
import Input from 'components/UI/Form/Input';
import NumberFormat from 'react-number-format';
import InputError from 'components/UI/Form/Error';

//Services
import { validateInput } from '../../helper';
import { isObjectEmpty } from 'utils';
import { useApplicationFormContext } from 'features/ApplicationForm/ApplicationFormProvider';

//STP Libraries

//Assets and Miscellaneous
import { initialValues, tenantTypes } from './constants';
import { IProposedFinance } from './types';
import { ReactSelectOption } from 'types/Constants';
import ReactSelect from 'components/UI/Form/ReactSelect';
import { Sources } from '../constants';
import { CurrencyFormatDisplay } from 'components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import { PercentFormatDisplay } from 'components/UI/NumberFormatDisplay/PercentFormatDisplay';

//Component Begin

const ProposedFinance = ({ formValues }: { formValues: any }) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editFinanceId, setEditFinanceId] = useState<string>('');
  const [selectedFinance, setSelectedFinance] = useState<IProposedFinance>();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<boolean>(false);
  const [finance, setFinance] = useState(initialValues);

  const { updateData, data } = useApplicationFormContext();

  const resetForm = () => {
    setFinance(initialValues);
  };

  const editFinanceHandler = (f: any) => {
    setIsEditing(true);
    const formData = Object.keys(finance).reduce((prev, next) => {
      return {
        ...prev,
        [next]: {
          ...finance[next],
          value: f[next],
          isTouched: f[next] ? true : false,
        },
      };
    }, {});
    setFinance(formData);
    setEditFinanceId(f.id);
  };

  const updateFinanceHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(finance).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return {
            ...prev,
            [next]: finance[next].value,
            id: editFinanceId,
          };
        },
        {}
      );
      updateData({
        ...data,
        step3: {
          ...formValues,
          section2: data.step3.section2.map((p: any) => {
            if (p.id === editFinanceId) {
              return formattedData;
            }
            return p;
          }),
        },
      });
      resetForm();
      setIsEditing(false);
    }
  };

  const removeFinanceHandler = (finance: IProposedFinance) => {
    setShowRemoveConfirm(true);
    setSelectedFinance(finance);
  };

  const confirmRemoveFinance = async () => {
    setShowRemoveConfirm(false);
    const filteredTenant = data.step3.section2.filter(
      (finance: any) => finance.id !== selectedFinance?.id
    );
    updateData({
      ...data,
      step3: {
        ...formValues,
        section2: filteredTenant,
      },
    });

    if (selectedFinance?.id === editFinanceId) {
      resetForm();
      setIsEditing(false);
    }
  };

  const cancelRemoveFinance = () => {
    setShowRemoveConfirm(false);
  };

  const validateForm = () => {
    const formData = { ...finance };

    const formDataErrors = Object.keys(formData).reduce(
      (prev: { [key: string]: string }, next: string) => {
        if (formData[next].error.length >= 1) {
          return {
            ...prev,
            [next]: formData[next].error,
          };
        }
        if (!formData[next].isTouched && formData[next].rules.required) {
          setFinance((prevState) => ({
            ...prevState,
            [next]: { ...prevState[next], error: 'Required' },
          }));
          return {
            ...prev,
            [next]: 'Required',
          };
        }
        return { ...prev };
      },
      {}
    );

    return isObjectEmpty(formDataErrors);
  };

  const onAddFinanceHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(finance).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return { ...prev, [next]: finance[next].value };
        },
        {}
      );
      updateData({
        ...data,
        step3: {
          ...formValues,
          section2: [
            ...data.step3.section2,
            { ...formattedData, id: uuidv4() },
          ],
        },
      });
      resetForm();
    }
  };

  const inputChangedHandler = (
    value:
      | string
      | number
      | boolean
      | ChangeEvent<HTMLInputElement>
      | undefined,
    name: string
  ) => {
    const inputValidation = validateInput(finance[name].rules, value);

    setFinance((prevState) => ({
      ...prevState,
      [name]: {
        ...prevState[name],
        value,
        isTouched: true,
        error: inputValidation.error,
      },
    }));
  };

  useEffect(() => {
    setFinance((prevState) => ({
      ...prevState,
      totalCost: {
        ...prevState['totalCost'],
        value: calculateTotalCost({
          eligibleCost: finance?.eligibleCost?.value,
          ineligibleCost: finance?.ineligibleCost?.value,
        }),
        isTouched: true,
      },
    }));
  }, [finance?.eligibleCost?.value, finance?.ineligibleCost?.value]);

  const calculateTotalCost = (data: any) => {
    let total = 0;
    if (data.eligibleCost) {
      total += data.eligibleCost;
    }

    if (data.ineligibleCost) {
      total += data.ineligibleCost;
    }
    return total;
  };

  return (
    <>
      {showRemoveConfirm && (
        <Confirm
          title='Remove finance'
          message={`You are about to remove ${selectedFinance?.source} finance. Are you sure you want to remove ?`}
          acceptButtonLabel='Remove'
          declineButtonLabel='Cancel'
          accept={confirmRemoveFinance}
          decline={cancelRemoveFinance}
        />
      )}
      <section className='space-y-4'>
        <div>
          <h3>Proposed Financing</h3>
          <p className='block text-sm text-neutralColor-normal'>
            Other sources of funding must be equal to or greater than 20% of the
            Façade Program Assistance amount.
          </p>
        </div>
        {data.step3?.section2.length > 0 && (
          <div className='space-y-2 w-full h-full ring-1 ring-neutralColor-lighter rounded-xl overflow-hidden'>
            <table className='divide-y divide-gray-200 w-full'>
              <thead className='bg-gray-50'>
                <tr>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>
                    Source
                  </th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>
                    % of Project Funding
                  </th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>
                    Rate
                  </th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>
                    Term
                  </th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>
                    Eligible Costs
                  </th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>
                    Ineligible Costs
                  </th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'>
                    Total Costs
                  </th>
                  <th className='p-4 text-left text-xs text-neutralColor-normal tracking-wider'></th>
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-gray-200'>
                {data.step3.section2.map((finance: IProposedFinance) => (
                  <tr
                    key={finance.id}
                    className={`${
                      finance.id === editFinanceId
                        ? 'bg-neutralColor-lighter'
                        : ''
                    }`}
                  >
                    <td className='p-4'>{finance.source}</td>
                    <td className='p-4'>
                      <PercentFormatDisplay
                        value={finance.projectFundingPercentage ?? 0}
                      />
                    </td>
                    <td className='p-4'>
                      <PercentFormatDisplay value={finance.rate ?? 0} />
                    </td>
                    <td className='p-4'>{finance.term}</td>
                    <td className='p-4'>
                      <CurrencyFormatDisplay
                        value={finance.eligibleCost ?? 0}
                      />
                    </td>
                    <td className='p-4'>
                      <CurrencyFormatDisplay value={finance.ineligibleCost ?? 0} />
                    </td>
                    <td className='p-4'>
                      <CurrencyFormatDisplay
                        value={calculateTotalCost(finance)}
                      />
                    </td>
                    <td className='flex justify-end gap-4 p-4'>
                      <div
                        className='cursor-pointer hover:text-blue-500'
                        onClick={() => editFinanceHandler(finance)}
                      >
                        Edit
                      </div>
                      <div
                        className='cursor-pointer hover:text-tertiaryColor-normal'
                        onClick={() => removeFinanceHandler(finance)}
                      >
                        Delete
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className='w-full grid grid-cols-6 gap-4'>
          <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
            <ReactSelect
              label='Source'
              id='source'
              name='source'
              options={
                data?.step3?.section2[0]?.source !== 'Façade Program Assistance'
                  ? Sources.filter(
                      (s: any) => s === 'Façade Program Assistance'
                    ).map((source) => ({
                      label: source,
                      value: source,
                    }))
                  : Sources.map((source) => ({
                      label: source,
                      value: source,
                    }))
              }
              value={{
                label: finance.source.value,
                value: finance.source.value,
              }}
              error={finance?.source?.error}
              onChange={(option) => {
                inputChangedHandler(
                  (option as ReactSelectOption).value,
                  'source'
                );
              }}
              onBlur={(e) => {
                inputChangedHandler(finance.source.value, 'source');
              }}
            />
          </div>
          <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
            <label
              className={`leading-7 block text-sm font-medium text-neutralColor-medium`}
              htmlFor='term'
            >
              Term
            </label>
            <NumberFormat
              name='term'
              id='term'
              value={finance.term.value ?? ''}
              className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-none'
              thousandSeparator={true}
              isNumericString
              maxLength={21}
              onValueChange={(target, s) => {
                if (s.source !== 'prop') {
                  inputChangedHandler(target.floatValue, 'term');
                }
              }}
              onBlur={(e: any) => {
                inputChangedHandler(finance.term.value, e.target.name);
              }}
            />
            {finance?.term?.error && (
              <InputError>{finance?.term?.error}</InputError>
            )}
          </div>
          <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
            <label
              className={`leading-7 block text-sm font-medium text-neutralColor-medium`}
              htmlFor='projectFundingPercentage'
            >
              % of Project Funding
            </label>
            <NumberFormat
              name='projectFundingPercentage'
              id='projectFundingPercentage'
              value={finance.projectFundingPercentage.value ?? ''}
              className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-none'
              isNumericString
              maxLength={3}
              onValueChange={(target, s) => {
                if (s.source !== 'prop') {
                  inputChangedHandler(
                    target.floatValue,
                    'projectFundingPercentage'
                  );
                }
              }}
              onBlur={(e: any) => {
                inputChangedHandler(
                  finance.projectFundingPercentage.value,
                  e.target.name
                );
              }}
            />
            {finance?.projectFundingPercentage?.error && (
              <InputError>
                {finance?.projectFundingPercentage?.error}
              </InputError>
            )}
          </div>
          <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
            <label
              className={`leading-7 block text-sm font-medium text-neutralColor-medium`}
              htmlFor='originalAmount'
            >
              Rate (%)
            </label>
            <NumberFormat
              name='rate'
              id='rate'
              value={finance.rate.value ?? ''}
              className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-none'
              isNumericString
              maxLength={3}
              onValueChange={(target, s) => {
                if (s.source !== 'prop') {
                  inputChangedHandler(target.floatValue, 'rate');
                }
              }}
              onBlur={(e: any) => {
                inputChangedHandler(finance.rate.value, e.target.name);
              }}
            />
            {finance?.rate?.error && (
              <InputError>{finance?.rate?.error}</InputError>
            )}
          </div>
          <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
            <label
              className={`leading-7 block text-sm font-medium text-neutralColor-medium`}
              htmlFor='originalAmount'
            >
              Eligible Cost ($)
            </label>
            <NumberFormat
              name='eligibleCost'
              id='eligibleCost'
              value={finance.eligibleCost.value ?? ''}
              className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-none'
              thousandSeparator={true}
              decimalScale={2}
              isNumericString
              prefix={'$ '}
              maxLength={21}
              onValueChange={(target, s) => {
                if (s.source !== 'prop') {
                  inputChangedHandler(target.floatValue, 'eligibleCost');
                }
              }}
              onBlur={(e: any) => {
                inputChangedHandler(finance.eligibleCost.value, e.target.name);
              }}
            />
            {finance?.eligibleCost?.error && (
              <InputError>{finance?.eligibleCost?.error}</InputError>
            )}
          </div>
          <div className='col-span-6 sm:col-span-3 lg:col-span-2'>
            <label
              className={`leading-7 block text-sm font-medium text-neutralColor-medium`}
              htmlFor='originalAmount'
            >
              Ineligible Cost ($)
            </label>
            <NumberFormat
              name='ineligibleCost'
              id='ineligibleCost'
              value={finance.ineligibleCost.value ?? ''}
              className='block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 sm:text-sm placeholder-gray-300 focus:outline-none'
              thousandSeparator={true}
              decimalScale={2}
              isNumericString
              prefix={'$ '}
              maxLength={21}
              onValueChange={(target, s) => {
                if (s.source !== 'prop') {
                  inputChangedHandler(target.floatValue, 'ineligibleCost');
                }
              }}
              onBlur={(e: any) => {
                inputChangedHandler(
                  finance.ineligibleCost.value,
                  e.target.name
                );
              }}
            />
            {finance?.ineligibleCost?.error && (
              <InputError>{finance?.ineligibleCost?.error}</InputError>
            )}
          </div>
        </div>
        {!isEditing && (
          <Button
            icon={<PlusIcon />}
            label='Add'
            type='button'
            btnStyle='primary'
            onClick={onAddFinanceHandler}
          />
        )}
        {isEditing && (
          <div className='flex gap-4'>
            <Button
              label='Save'
              type='button'
              btnStyle='primary'
              onClick={updateFinanceHandler}
            />
            <Button
              label='Cancel'
              type='button'
              btnStyle='neutral'
              onClick={() => {
                resetForm();
                setEditFinanceId('');
                setIsEditing(false);
              }}
            />
          </div>
        )}
      </section>
    </>
  );
};

export default ProposedFinance;
